import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useNotifications } from 'app/application/application'
import { useApplicationStore } from 'app/application/infrastructure'
import { useApi } from 'app/common/application'

import {
  QueryObjectivesResponse,
  StoreObjectiveContent,
  StoreObjectiveRequest,
  StoreObjectiveResponse,
} from '../types'

const useObjectives = () => {
  const { create, read } = useApi()
  const { createNotification } = useNotifications()
  const { t } = useTranslation('error')

  const objectives = useApplicationStore((state) => state.objectives)
  const addObjective = useApplicationStore((state) => state.addObjective)
  const setObjectives = useApplicationStore((state) => state.setObjectives)

  const fetchObjectives = useCallback(async () => {
    const result = await read<QueryObjectivesResponse>('/client/v1/objectives')

    if (result.ok) {
      setObjectives(result.val.items)
    } else {
      createNotification({
        message: result.val.message,
        title: t(`http.${result.val.code}`),
        type: 'error',
      })
    }

    return Promise.resolve()
  }, [createNotification, read, setObjectives, t])

  const getObjective = useCallback(
    (id: string) => objectives.find((objective) => objective.id === id) ?? null,
    [objectives],
  )

  const storeObjective = useCallback(
    async (content: StoreObjectiveContent) => {
      const result = await create<StoreObjectiveRequest, StoreObjectiveResponse>(
        '/client/v1/objectives',
        content,
      )

      if (result.ok) {
        addObjective(result.val)
      } else {
        createNotification({
          message: result.val.message,
          title: t(`http.${result.val.code}`),
          type: 'error',
        })
      }

      return result
    },
    [addObjective, create, createNotification, t],
  )

  return {
    fetchObjectives,
    getObjective,
    objectives,
    storeObjective,
  }
}

export default useObjectives
